import { IconName } from '@interco/inter-toranja'

type LocaleType = {
  header: {
    title: string
    subtitle: string
  }
  smartFinancing: {
    title: string
    items: {
      label: string
      icon: IconName
    }[]
  }
  whyChooseInter: {
    title: string
    items: {
      title: string
      description: string
      icon: IconName
    }[]
  }
  actionButton: {
    title: string
  }
}

type LocaleInput = {
  header: LocaleType['header']
  smartFinancing: {
    title: string
    labels: string[]
  }
  whyChooseInter: {
    title: string
    items: { title: string; description: string }[]
  }
  actionButton: LocaleType['actionButton']
}

const SMART_FINANCING_ICONS: IconName[] = [
  'ic_hand_money',
  'ic_sliders',
  'ic_list',
  'ic_messages',
  'ic_house_trending_up',
]

const WHY_CHOOSE_INTER_ICONS: IconName[] = ['ic_star', 'ic_document', 'ic_sliders']

const buildLocale = ({
  header,
  smartFinancing,
  whyChooseInter,
  actionButton,
}: LocaleInput): LocaleType => ({
  header,
  smartFinancing: {
    title: smartFinancing.title,
    items: smartFinancing.labels.map((label, i) => ({ label, icon: SMART_FINANCING_ICONS[i] })),
  },
  whyChooseInter: {
    title: whyChooseInter.title,
    items: whyChooseInter.items.map((item, i) => ({ ...item, icon: WHY_CHOOSE_INTER_ICONS[i] })),
  },
  actionButton,
})

export const locales: Record<string, LocaleType> = {
  'pt-BR': buildLocale({
    header: {
      title: 'Financiamento imobiliário',
      subtitle: 'Soluções para comprar e investir nos Estados Unidos',
    },
    smartFinancing: {
      title: 'Financiamento inteligente',
      labels: [
        'Orientações de especialistas licenciados nos Estados Unidos',
        'Opções para compradores e investidores',
        'Condições claras e etapas transparentes',
        'Atendimento no idioma de sua preferência',
        'E muito mais',
      ],
    },
    whyChooseInter: {
      title: 'Por que financiar seu imóvel com a Inter?',
      items: [
        {
          title: 'Expertise que entende seus objetivos',
          description:
            'Assistência personalizada para navegar com confiança no mercado imobiliário dos Estados Unidos.',
        },
        {
          title: 'Condições claras e transparentes',
          description:
            'Sem surpresas. Apenas orientações diretas ao longo de todo o processo de financiamento.',
        },
        {
          title: 'Opções de financiamento que cabem no seu plano',
          description:
            'De residências principais a imóveis para investimento, encontre o crédito ideal para você.',
        },
      ],
    },
    actionButton: { title: 'Estou interessado' },
  }),

  'en-US': buildLocale({
    header: {
      title: 'Mortgage',
      subtitle: 'Solutions for buying or investing in the U.S.',
    },
    smartFinancing: {
      title: 'Smart financing',
      labels: [
        'Guidance from licensed U.S. mortgage specialists',
        'Options for buyers and investors',
        'Clear terms, transparent steps',
        'Support in your preferred language',
        'And a lot more',
      ],
    },
    whyChooseInter: {
      title: 'Why finance your property with Inter?',
      items: [
        {
          title: 'Expertise that understands your goals',
          description:
            'Personalized assistance to help you navigate U.S. real estate finance with confidence.',
        },
        {
          title: 'Clear, transparent terms',
          description:
            'No hidden surprises. Just straightforward guidance throughout your mortgage process.',
        },
        {
          title: 'Financing options that fit your plan',
          description:
            'From primary homes to investment properties, find the loan that matches your vision.',
        },
      ],
    },
    actionButton: { title: 'I am interested' },
  }),

  'es-ES': buildLocale({
    header: {
      title: 'Hipoteca en EE. UU.',
      subtitle: 'Soluciones hipotecarias para comprar o investir en EE. UU.',
    },
    smartFinancing: {
      title: 'Financiamiento inteligente',
      labels: [
        'Asesoría de especialistas hipotecarios con licencia en EE. UU.',
        'Opciones para compradores e inversionistas',
        'Términos claros, pasos transparentes',
        'Soporte en tu idioma preferido',
        'Y mucho más',
      ],
    },
    whyChooseInter: {
      title: '¿Por qué financiar tu propiedad con Inter?',
      items: [
        {
          title: 'Experiencia que entiende tus objetivos',
          description:
            'Asistencia personalizada para que navegues el mercado inmobiliario de EE. UU. con confianza.',
        },
        {
          title: 'Términos claros y transparentes',
          description:
            'Sin sorpresas ocultas. Solo orientación directa durante todo tu proceso hipotecario.',
        },
        {
          title: 'Opciones de financiamiento que se adaptan a tu plan',
          description:
            'Desde viviendas principales hasta propiedades de inversión, encuentra el préstamo ideal para ti.',
        },
      ],
    },
    actionButton: { title: 'Estoy interesado' },
  }),
}
